import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Attachment from './attachment';
import EmptyState from 'shared/empty_state';
import FileSystem from 'components/file_system';
import Folder from './folder';
import { excludedColumns, fileSystemAdditionalColumns, fileSystemAttributeOverrides } from './config';
import { findParentFolder, handleDrag, replace, sort } from './helpers';
import { renameAttachment, setAttachmentFolder, setFolderParent } from './api';

export default function AttachmentsTable({
  attachments,
  canDeleteOwnAttachments,
  canManageAllAttachments,
  currentFolder,
  folders,
  forUser,
  onAttachmentsChange,
  onFileClick,
  onFolderClick,
  onFoldersChange,
}) {
  const [sortOrder, setSortOrder] = useState({});
  const [dragIndex, setDragIndex] = useState();
  const [dropIndex, setDropIndex] = useState();

  const sortedAttachments = useMemo(() => {
    const visibleAttachments = attachments.filter(attachment => attachment.folderId == currentFolder.id);
    return sort(visibleAttachments, sortOrder);
  }, [attachments, currentFolder, sortOrder]);

  const sortedFolders = useMemo(() => {
    const visibleFolders = folders.filter(folder => folder.parentFolderId == currentFolder.id);
    return sort(visibleFolders, sortOrder);
  }, [currentFolder, folders, sortOrder]);

  const folderChildCountsById = useMemo(() => {
    const counts = new Map();
    folders.forEach((folder) => {
      if (!folder.parentFolderId) return;

      counts.set(folder.parentFolderId, (counts.get(folder.parentFolderId) ?? 0) + 1);
    });
    attachments.forEach((attachment) => {
      if (!attachment.folderId) return;

      counts.set(attachment.folderId, (counts.get(attachment.folderId) ?? 0) + 1);
    });

    return counts;
  }, [folders, attachments]);

  function moveAttachmentUp(attachment) {
    const folder = findParentFolder(folders, attachment.folderId);
    handleAttachmentChange(setAttachmentFolder(attachment, folder));
  }

  function handleAttachmentChange(attachment) {
    onAttachmentsChange(replace(attachments, attachment));
  }

  function moveFolderUp(folder) {
    const parent = findParentFolder(folders, folder.id);
    const grandparent = findParentFolder(folders, parent.id);
    handleFolderChange(setFolderParent(folder, grandparent));
  }

  function handleFolderChange(folder) {
    onFoldersChange(replace(folders, folder));
  }

  function handleFileRename(attachment, name) {
    handleAttachmentChange(renameAttachment(attachment, name));
  }

  function handleHeaderClick(key) {
    if (key === sortOrder.key) {
      setSortOrder({ ...sortOrder, reverse: !sortOrder.reverse });
    } else {
      setSortOrder({ key });
    }
  }

  const dndProps = {
    onBeginDrag: setDragIndex,
    onReorder(_, dropIndex) {
      setDropIndex(dropIndex);
    },
    onEndDrag() {
      const result = handleDrag(sortedAttachments, sortedFolders, dragIndex, dropIndex);
      if (result.attachment) {
        handleAttachmentChange(result.attachment);
      } else if (result.folder) {
        handleFolderChange(result.folder);
      }
    }
  };

  function ownsAttachment(attachment) {
    return forUser.id === attachment.uploadedById;
  }

  function isAllowedToDeleteAttachment(attachment) {
    return canManageAllAttachments || (canDeleteOwnAttachments && ownsAttachment(attachment));
  }

  function isAllowedToRenameAttachment(attachment) {
    return canManageAllAttachments || ownsAttachment(attachment);
  }

  function isAllowedToDeleteFolder(folder) {
    return canManageAllAttachments || !folderChildCountsById.has(folder.id);
  }

  if (!sortedFolders.length && !sortedAttachments.length) {
    return (
      <EmptyState
        bodyText="No attachments have been uploaded"
      />
    );
  }

  return (
    <div aria-label="table">
      <FileSystem.Table
        additionalColumns={fileSystemAdditionalColumns}
        attributeOverrides={fileSystemAttributeOverrides}
        excludedColumns={excludedColumns}
        sort={sortOrder}
        onHeaderClick={handleHeaderClick}
      >
        {sortedFolders.map((folder, i) => (
          <Folder
            deletable={isAllowedToDeleteFolder(folder)}
            folder={folder}
            index={i}
            key={i}
            onChange={handleFolderChange}
            onClick={onFolderClick}
            onDelete={deleted => onFoldersChange(folders.filter(folder => folder.id !== deleted.id))}
            onMoveUp={moveFolderUp}
            {...dndProps}
          />
        ))}

        {sortedAttachments.map((attachment, i) => (
          <Attachment
            attachment={attachment}
            deletable={isAllowedToDeleteAttachment(attachment)}
            index={sortedFolders.length + i}
            key={i}
            onDelete={deleted => onAttachmentsChange(attachments.filter(attachment => attachment.id !== deleted.id))}
            onMoveUp={moveAttachmentUp}
            onPreview={onFileClick ? (() => onFileClick(attachment)) : null}
            onRename={isAllowedToRenameAttachment(attachment) ? handleFileRename : null}
            {...dndProps}
          />
        ))}
      </FileSystem.Table>
    </div>
  );
}

AttachmentsTable.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.shape({
    folderId: PropTypes.number,
  })).isRequired,
  canDeleteOwnAttachments: PropTypes.bool,
  canManageAllAttachments: PropTypes.bool,
  currentFolder: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  folders: PropTypes.arrayOf(PropTypes.shape({
    parentFolderId: PropTypes.number,
  })).isRequired,
  forUser: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  onAttachmentsChange: PropTypes.func.isRequired,
  onFileClick: PropTypes.func,
  onFolderClick: PropTypes.func,
  onFoldersChange: PropTypes.func.isRequired,
};
